import { Backdrop, Button, CircularProgress, Dialog, DialogActions, DialogContent, Grid, Paper, Snackbar, SnackbarContent, Typography } from '@material-ui/core';
import { useEffect, useState } from "react";
import { v4 as uuid } from 'uuid';
import { geminiBooleanResponseMimeType, geminiBooleanResponseSchema, geminiConfig, geminiFileResponseMimeType, geminiFileResponseSchema } from "./Utils/GeminiHandler";
import useSocketClient from "./Utils/SocketClient";
import { getDistributorsForPrompt, getManufacturersForPrompt, getSystemInstructionsForPrompt, getPromptToken } from '../../Actions';
import Chat from './Chat';
import FileUpload from './FileUpload';
import Form from './Form';
import AutoProcessPreview from './AutoProcessPreview';
import { fileToBase64 } from './Utils/Helpers';

const AUTO_PROCESS_STARTING_MESSAGE = 'That is perfect! Now please convert the entire file using the output you provided for the first record as a template. The manufacturer and distributor should remain the same unless the file explicitly states otherwise.';
const AUTO_PROCESS_FIRST_CHECK_MESSAGE = 'The whole file needs to be converted for processing. Are you sure you finished converting the entire file?';
const AUTO_PROCESS_SECOND_CHECK_MESSAGE = 'Please double check. Are you sure you finished converting the entire file?';
const AUTO_PROCESS_CONTINUE_MESSAGE = 'Continue converting the file from where you left off and do not to duplicate records that have already been converted.';
const SOCKET_RETRY_LIMIT = 5;

export default function Prompt () {
  const [selectedSessionId, setSelectedSessionId] = useState(() => uuid());
  const [sessionHistory, setSessionHistory] = useState([]);
  const [sessionName, setSessionName] = useState('');
  const [sessionType, setSessionType] = useState('new');
  const [tokenCount, setTokenCount] = useState(0);
  const [token, setToken] = useState();
  const [socketRetryCount, setSocketRetryCount] = useState(0);
  const [maxRequestSize, setMaxRequestSize] = useState();

  const [distributorList, setDistributorList] = useState([]);
  const [manufacturerList, setManufacturerList] = useState([]);

  const [message, setMessage] = useState('');
  const [responseData, setResponseData] = useState();
  const [defaultSI, setDefaultSI] = useState('');
  const [systemInstructions, setSystemInstructions] = useState('');
  const [isFormRequest, setIsFormRequest] = useState(false);
  const [attachedFile, setAttachedFile] = useState();
  const [initialDataRecord, setInitialDataRecord] = useState();
  const [selectedDistributor, setSelectedDistributor] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState([]);
  
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [awaitingResponseMessage, setAwaitingResponseMessage] = useState('');
  const [isLoadingPageData, setIsLoadingPageData] = useState(true);
  const [errorData, setErrorData] = useState();
  const [showLoadingBackdrop, setShowLoadingBackdrop] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [step, setStep] = useState(0);
  const [isProcessingStopped, setIsProcessingStopped] = useState(false);

  const { socket, emit, on } = useSocketClient(process.env.NODE_ENV === 'production' ? 'https://api-hub.showroompricing.com' : 'http://localhost:4000', token);

  const updateConfig = (systemInstructions) => {
    const config = geminiConfig;

    if (systemInstructions) {
      config.systemInstructions = systemInstructions;
    }

    emit('session:updateConfig', config);
  }

  useEffect(() => {
    async function getInitialPageData() {
      await getDistributorsForPrompt().then(response => {
        setDistributorList([ ...response.data.distributors ]);
      }).catch(err => {
        setDistributorList([]);
      });

      await getManufacturersForPrompt().then(response => {
        setManufacturerList([ ...response.data.manufacturers ]);
      }).catch(err => {
        setManufacturerList([]);
      });

      await getSystemInstructionsForPrompt().then(response => {
        setDefaultSI(response.data.data);
        setSystemInstructions(response.data.data);
        
        updateConfig(response.data.data);
      }).catch(err => {
        setDefaultSI('');
        setSystemInstructions('');
      });

      setIsLoadingPageData(false);
    }

    if (!isLoadingPageData) return;
    
    getInitialPageData();
  }, [])

  useEffect(() => {
    if (socketRetryCount < SOCKET_RETRY_LIMIT) return;

    socket?.disconnect();
    setErrorData({ error: 'The client was unable to connect to the server at this time. Please try again later or refresh the page.' });
  }, [setSocketRetryCount])

  useEffect(() => {
    async function getSessionToken(sessionId) {
      const result = await getPromptToken(sessionId);

      setToken(result.data.token);
    }

    if (!selectedSessionId) return;

    setSessionName(selectedSessionId)
    getSessionToken(selectedSessionId);
  }, [selectedSessionId])

  useEffect(() => {
    if (!responseData) return;

    const sentMessage = sessionHistory?.find(m => m.messageId === responseData.messageId);

    setSessionHistory(prev => {
      const filteredHistory = [ ...prev ];

      if (responseData?.fileId) {
        const fileDataIndex = prev?.findIndex(m => m.messageId === responseData.fileId);
        filteredHistory[fileDataIndex].tokenCount = responseData.fileTokenCount;
      }

      if (responseData?.messageId) {
        const messageDataIndex = prev?.findIndex(m => m.messageId === responseData.messageId);
        filteredHistory[messageDataIndex].tokenCount = responseData.messageTokenCount;
      }

      const response = responseData.response;
      if (sentMessage?.content?.data.includes(AUTO_PROCESS_STARTING_MESSAGE) || sentMessage?.content?.data.includes(AUTO_PROCESS_CONTINUE_MESSAGE)) {
        response.content.isFileOutputPart = true;
      }

      filteredHistory.push(response);

      return [ ...filteredHistory ];
    });

    setTokenCount(prev => {
      const messageCount = responseData.messageTokenCount ?? 0;
      const fileCount = responseData.fileTokenCount ?? 0;
      const responseCount = responseData.response.tokenCount ?? 0;
      const thinkingCount = responseData.response.thinkingTokenCount ?? 0;

      return prev + messageCount + fileCount + responseCount + thinkingCount;
    });

    if (message) setMessage('');
    if (attachedFile) setAttachedFile(undefined);

    if (step !== 1 || !isFormRequest || isProcessingStopped) {
      setResponseData(undefined);
      setIsAwaitingResponse(false);
      setIsProcessingStopped(false);

      return;
    }

    if (sentMessage?.content?.data.includes(AUTO_PROCESS_STARTING_MESSAGE) || sentMessage?.content?.data.includes(AUTO_PROCESS_CONTINUE_MESSAGE)) {
      const firstCheckMessage = {
        tokenCount: 0,
        thinkingTokenCount: 0,
        messageId: uuid(),
        content: {
          role: 'user',
          data: AUTO_PROCESS_FIRST_CHECK_MESSAGE,
          type: 'text'
        }
      };

      setAwaitingResponseMessage(sentMessage?.content?.data.includes(AUTO_PROCESS_STARTING_MESSAGE) ? 'Checking to make sure the file was fully converted' : 'Rechecking to make sure the file was fully converted');
      setSessionHistory(prev => [ ...prev, firstCheckMessage ]);
      emit(
        "session:message", 
        { 
          sessionHistory: sessionHistory,
          message: firstCheckMessage, 
          file: undefined, 
          responseMimeType: geminiBooleanResponseMimeType, 
          responseSchema: geminiBooleanResponseSchema 
        }
      );

      return;
    }

    const check = responseData.response.content.data === 'yes';

    if (sentMessage?.content?.data.includes(AUTO_PROCESS_FIRST_CHECK_MESSAGE)) {
      const secondCheckMessage = {
        tokenCount: 0,
        thinkingTokenCount: 0,
        messageId: uuid(),
        content: {
          role: 'user',
          data: check ? AUTO_PROCESS_SECOND_CHECK_MESSAGE : AUTO_PROCESS_CONTINUE_MESSAGE,
          type: 'text'
        }
      };

      setAwaitingResponseMessage(check ? 'Double checking the conversion has finished' : 'Looks like the conversion is not finished, continuing the process');
      setSessionHistory(prev => [ ...prev, secondCheckMessage ]);
      emit(
        "session:message", 
        { 
          sessionHistory: sessionHistory,
          message: secondCheckMessage, 
          file: undefined, 
          responseMimeType: check ? geminiBooleanResponseMimeType : geminiFileResponseMimeType, 
          responseSchema: check ? geminiBooleanResponseSchema : geminiFileResponseSchema
        }
      );

      return;
    }

    if (sentMessage?.content?.data.includes(AUTO_PROCESS_SECOND_CHECK_MESSAGE) && !check) {
      const continueMessage = {
        tokenCount: 0,
        thinkingTokenCount: 0,
        messageId: uuid(),
        content: {
          role: 'user',
          data: AUTO_PROCESS_CONTINUE_MESSAGE,
          type: 'text'
        }
      };

      setAwaitingResponseMessage('Looks like the conversion is not finished, continuing the process');
      setSessionHistory(prev => [ ...prev, continueMessage ]);
      emit(
        "session:message", 
        {
          sessionHistory: sessionHistory,
          message: continueMessage, 
          file: undefined, 
          responseMimeType: geminiFileResponseMimeType, 
          responseSchema: geminiFileResponseSchema
        }
      );

      return;
    }

    setResponseData(undefined);
    setIsAwaitingResponse(false);
  }, [responseData, step])

  useEffect(() => {  
    if (!socket) return;
    
    on("connect", () => {
      console.log('Socket connected!');
      updateConfig(systemInstructions);
      setErrorData(undefined);
      setSocketRetryCount(0);

      // if (sessionHistory?.length) {
      //   emit('session:updateHistory', sessionHistory);
      // }
    });

    on("reconnect", () => {
      console.log('Socket reconnected!')
      updateConfig(systemInstructions);
      setErrorData(undefined);

      // if (sessionHistory?.length) {
      //   emit('session:updateHistory', sessionHistory);
      // }
    });

    on("connect_error", (err) => {
      setSocketRetryCount(prev => prev + 1);
    });

    on("disconnect", (reason, details) => {
      // console.log('Socket disconnected!', reason, details)
      setErrorData({ reason, details });
    });

    on('session:maxRequestSize', (size) => {
      setMaxRequestSize(size);
    });

    on('session:ok', (data) => {
      console.log('ok', data);
    })

    on('session:warning', (data) => {
      console.log('warning', data);
    })

    on('session:error', (data) => {
      setErrorData({ data });
      setIsFormRequest(false);
      setIsProcessingStopped(true);
      setIsAwaitingResponse(false);
      setAwaitingResponseMessage('');
    })

    on('session:response', (data) => {
      setResponseData({ ...data });
    })
  }, [socket, on])

  const submitPrompt = async (history, message, attachedFile) => {
    const messageData = {
      tokenCount: 0,
      thinkingTokenCount: 0,
      messageId: uuid(),
      content: {
        role: 'user',
        data: message,
        type: 'text'
      }
    };

    let fileData;

    if (attachedFile) {
      const file = await fileToBase64(attachedFile);
      let messageType = 'text';

      switch (attachedFile.type) {
        case 'text/csv':
          messageType = 'csv';
          break;
        case 'application/pdf':
          messageType = 'pdf';
          break;
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'application/vnd.oasis.opendocument.spreadsheet': 
        case 'application/vnd.google-apps.spreadsheet': 
        case 'application/vnd.ms-excel':
          messageType = 'xlsx';
          break;
        default: 
          messageType = 'text';
      }

      fileData = {
        tokenCount: 0,
        thinkingTokenCount: 0,
        messageId: uuid(),
        content: {
          role: 'user',
          data: file,
          type: messageType,
          mimeType: attachedFile.type,
          filename: attachedFile.name
        }
      };
    }

    setSessionHistory(prev => {
      return fileData ? [ ...prev, fileData, messageData ] : [ ...prev, messageData ];
    });

    emit(
      "session:message", 
      { 
        sessionHistory: history,
        message: messageData, 
        file: fileData, 
        responseMimeType: geminiFileResponseMimeType, 
        responseSchema: geminiFileResponseSchema 
      }
    );
  }

  const reloadStep = () => {
    switch (step) {
      case 1:
        setTokenCount(0);

        const newSessionHistory = [ ...sessionHistory.slice(0, -1) ];
        setSessionHistory(newSessionHistory);

        emit(
          "session:message", 
          { 
            sessionHistory: sessionHistory,
            message: newSessionHistory[1], 
            file: newSessionHistory[0], 
            responseMimeType: geminiFileResponseMimeType, 
            responseSchema: geminiFileResponseSchema 
          }
        );

        break;
      case 2:
        const autoProcessStart = sessionHistory.findIndex(m => m.content.data.includes(AUTO_PROCESS_STARTING_MESSAGE));

        if (autoProcessStart === -1) return;

        const autoProcessHistory = sessionHistory.filter((m, i) => i >= autoProcessStart);
        const autoProcesstokenCount = autoProcessHistory.reduce((acc, curr) => {
          return acc + curr.tokenCount + curr.thinkingTokenCount;
        }, 0);
        const filteredHistory = sessionHistory.slice(0, autoProcessStart);

        setTokenCount(autoProcesstokenCount);
        setSessionHistory([ ...filteredHistory, sessionHistory[autoProcessStart] ]);
        emit(
          "session:message", 
          { 
            sessionHistory: filteredHistory,
            message: sessionHistory[autoProcessStart], 
            file: undefined, 
            responseMimeType: geminiFileResponseMimeType, 
            responseSchema: geminiFileResponseSchema 
          }
        );

        break;
      default:
        break;
    }
  }

  useEffect(() => {
    if (isAwaitingResponse || !isFormRequest) return;

    switch (step) {
      case 0:
        const intialRecord = sessionHistory[sessionHistory.length - 1];

        const record = JSON.parse(intialRecord?.content.data)?.[0] ?? {};

        const transformedData = {};
        
        Object.keys(geminiFileResponseSchema.items.properties).forEach(r => {
          if (!record[r] || (record[r] && record[r] === 'null')) {
            transformedData[r] = null;
            return;
          }
          
          transformedData[r] = record[r] || null;
          return;
        });

        setInitialDataRecord({ ...transformedData });

        break;        
      default:
        break;
    }

    setStep(prev => prev + 1);
    setShowChat(false);
    setIsFormRequest(false);
  }, [isAwaitingResponse, isFormRequest, step])

  const clearChat = async () => {
    setAttachedFile(undefined);
    setSessionHistory([]);
    setSelectedDistributor(undefined);
    setSelectedManufacturer(undefined);
    setSelectedSessionId(uuid());
    setTokenCount(0);
    setSessionType('new');
    setSessionName('');
    setShowChat(false);
    setMessage('');
    setSystemInstructions(defaultSI);
    setStep(0);
    setResponseData(undefined);
    setAwaitingResponseMessage('');
    setIsFormRequest(false);
    setIsAwaitingResponse(false);
    setErrorData(undefined);
  }

  let uiForStep; 

  switch (step) {
    case 0: 
      uiForStep = (
        <FileUpload 
          file={attachedFile}
          setFile={(file) => {
            if (!sessionName || sessionName === attachedFile?.name || /^(-{0,1}\w+){5}$/.test(sessionName)) {
              setSessionName(file?.name ?? selectedSessionId);
            }

            setAttachedFile(file);
          }}
          maxRequestSize={maxRequestSize}
          continueToNextStep={async () => {
            setAwaitingResponseMessage('Converting the first record for verification');
            setIsAwaitingResponse(true);
            setIsFormRequest(true);
            await submitPrompt(sessionHistory, 'Convert only the first record in the file', attachedFile);
          }}
        />
      )

      break;
    case 1:
      uiForStep = (
        <Form 
          sessionName={sessionName}
          setSessionName={(name) => setSessionName(name)}
          sessionType={sessionType}
          setSessionType={(type) => setSessionType(type)}
          initialDataRecord={initialDataRecord}
          setInitialDataRecord={(data) => setInitialDataRecord({ ...data })}
          distributorList={distributorList}
          selectedDistributor={selectedDistributor}
          setSelectedDistributor={(id) => setSelectedDistributor(id)}
          manufacturerList={manufacturerList}
          selectedManufacturer={selectedManufacturer}
          setSelectedManufacturer={(id) => setSelectedManufacturer(id)}
          reloadStep={() => {
            setAwaitingResponseMessage('Reconverting the first record for verification');
            setIsAwaitingResponse(true);
            setIsFormRequest(true);
            setStep(prev => prev - 1);
            reloadStep();
          }}
          continueToNextStep={async () => {
            setAwaitingResponseMessage('Starting conversion of the full file');
            setIsAwaitingResponse(true);
            setIsFormRequest(true);

            const sessionHistoryCopy = [ ...sessionHistory ];

            sessionHistoryCopy[sessionHistoryCopy.length - 1].content.data = JSON.stringify([initialDataRecord]);

            setSessionHistory([ ...sessionHistoryCopy ])
            await submitPrompt(sessionHistoryCopy, `${AUTO_PROCESS_STARTING_MESSAGE}`);
          }}
        />
      )
      
      break;
    case 2:
      uiForStep = (
        <AutoProcessPreview 
          sessionHistory={sessionHistory}
          reloadStep={() => {
            setAwaitingResponseMessage('Reprocessing the full file conversion');
            setIsAwaitingResponse(true);
            setIsFormRequest(true);
            setStep(prev => prev - 1);
            reloadStep();
          }}
          finishConversion={() => {
            clearChat();
          }}
        />
      )
      
      break;
    default: 
      uiForStep = (
        <></>
      )

      break;
  }

  return (
    <div style={{padding: "30px 20px"}}>
      <Paper style={{padding: "20px"}}>
        <div style={{position: "relative", padding: "15px"}}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <Typography variant="h4">Pricing File Convertor</Typography>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <Button 
                variant="contained" 
                size="small" 
                style={{margin: "10px 20px 10px 0px"}} 
                onClick={() => setShowChat(!showChat)}
              >
                {showChat ? 'Hide Chat' : 'Show Chat'}
              </Button>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => clearChat()}
                style={{margin: "10px 20px 10px 0px", backgroundColor: "#FFFFFF"}}
              >
                New Session
              </Button>
            </Grid>
          </Grid>  
          { showChat ? (
              <Chat 
                sessionHistory={sessionHistory}
                tokenCount={tokenCount}
                message={message}
                systemInstructions={systemInstructions}
                setSystemInstructions={(si) => setSystemInstructions(si)}
                setMessage={(value) => setMessage(value)}
                deleteMessage={(messageId) => {
                  const index = sessionHistory?.findIndex(s => s.messageId === messageId);

                  if (index === -1) return;

                  const newSessionHistory = [ ...sessionHistory.splice(index, 1) ];
                  setSessionHistory([ ...newSessionHistory ]);
                  // emit('session:updateHistory', newSessionHistory);
                }}
                sendMessage={() => {
                  setIsAwaitingResponse(true);
                  submitPrompt(sessionHistory, message, attachedFile);
                }}
              />
            ) : (
              <>
                <Grid item xs={12} md={12} lg={12} container spacing={3} style={{backgroundColor: "#f5f5f5", borderRadius: "10px", margin: "10px 0px 0px"}}>
                  {uiForStep}
                </Grid>
              </>
            )
          }
        </div>
      </Paper>
      { showLoadingBackdrop || isLoadingPageData ? (
          <Backdrop open={isAwaitingResponse || isLoadingPageData} style={{zIndex: "30", textAlign: "center", backgroundColor: "#142e3e", color: "#28c4fc"}}>
            <div>
              <CircularProgress size={100} color="inherit" />
              <Typography variant='h4' style={{color: "inherit", marginTop: "20px"}}>
                { isProcessingStopped ? "Stopped, Awaiting Response..." : "Loading..."}
              </Typography>
              { awaitingResponseMessage ? (
                  <Typography variant='h6' style={{color: "inherit", marginTop: "20px"}}>{awaitingResponseMessage}</Typography>
                ) : undefined
              }
              { !isLoadingPageData ? (
                  <Button variant="outlined" style={{marginTop: "20px", marginRight: "20px", color: "#FFFFFF", borderColor: "#FFFFFF"}} onClick={() => setShowLoadingBackdrop(false)}>Minimize Loader</Button>
                ) : undefined 
              }
              { !isLoadingPageData && step === 1 ? (
                  <Button 
                    variant="outlined" 
                    style={{marginTop: "20px", color: "#FFFFFF", borderColor: "#FFFFFF"}} 
                    onClick={() => {
                      setIsProcessingStopped(true);
                      setAwaitingResponseMessage('');
                    }}
                  >
                    Stop Processing
                  </Button>
                ) : undefined 
              }
            </div>
          </Backdrop>
        ) : (
          <Snackbar 
            open={isAwaitingResponse || isLoadingPageData}
            anchorOrigin={{vertical: "bottom", horizontal: "left"}}
          >
            <SnackbarContent 
              style={{backgroundColor: "#142e3e", color: "#28c4fc"}}
              message={(
                <>
                  <Typography variant='h6' color='inherit'>
                    <CircularProgress size={20} style={{display: "inline-block", verticalAlign: "center", color: "inherit", marginRight: "10px"}}/> 
                    { isProcessingStopped ? "Stopped, Awaiting Response..." : "Loading..."}
                  </Typography>
                  <Typography variant="caption">{awaitingResponseMessage}</Typography>
                </>
              )}
              action={(
                <>
                  { !isLoadingPageData ? (
                      <Button variant="outlined" style={{margin: "0px 10px", color: "#FFFFFF", borderColor: "#FFFFFF"}} onClick={() => setShowLoadingBackdrop(true)}>Maximize</Button>
                    ) : undefined 
                  }
                  { !isLoadingPageData && step === 1 ? (
                      <Button 
                        variant="outlined" 
                        style={{margin: "0px 10px", color: "#FFFFFF", borderColor: "#FFFFFF"}} 
                        onClick={() => {
                          setIsProcessingStopped(true);
                          setAwaitingResponseMessage('');
                        }}
                      >
                        Stop
                      </Button>
                    ) : undefined 
                  }
                </>
              )}
            />
          </Snackbar>
        )
      }
      <Dialog open={!!errorData} maxWidth="md">
        <DialogContent style={{textAlign: "center", padding: "20px 20px 0px"}}>
          <Typography variant='h6'>Oops! Looks like an error occurred!</Typography>
          <pre style={{textAlign: "left", whiteSpace: "pre-wrap", wordBreak: "break-all", padding: "10px", backgroundColor: "#f5f5f5"}}>
            <code>
              {JSON.stringify(errorData, null, 2)}
            </code>
          </pre>
        </DialogContent>
        <DialogActions style={{padding: "0px 20px 20px"}}>
          <Button 
            variant="contained"
            style={{margin: "10px 0px 0px 20px"}}
            onClick={() => {
              setErrorData(undefined);

              if (step === 1) {
                setStep(2);
                setShowChat(false);
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
